import { Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Table } from 'reactstrap';
import React, { useState, useCallback } from 'react';
import * as yup from 'yup';

import { Button } from '../../components';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { NumberOfActiveProgramsReportDto } from '../../common/Types';
import { reportService } from '../../services/ReportService';
import moment from 'moment';

// Form data interface
interface NumberOfActiveProgramsReportFormData {
    cutOffDate: string;
    concentrations: boolean;
}

// Validation schema
const validationSchema = yup.object({
    cutOffDate: yup.string().required('Cut off date is required'),
    concentrations: yup.boolean().required().default(false),
});

const NumberOfActiveProgramsReport = () => {
    const [actionTotals, setActionTotals] = useState<NumberOfActiveProgramsReportDto>();

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<NumberOfActiveProgramsReportFormData>({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        defaultValues: {
            cutOffDate: '',
            concentrations: false,
        },
    });

    const watchedValues = watch();

    const onSubmit = useCallback((data: NumberOfActiveProgramsReportFormData) => {
        const toastId = toast.info('Searching...', {
            autoClose: false,
        });
        reportService
            .getNumberOfActiveProgramsReport(moment(data.cutOffDate).format(), data.concentrations)
            .then((response) => {
                setActionTotals(response);
                toast.update(toastId, {
                    render: 'Report generated successfully.',
                    type: 'success',
                    autoClose: 500,
                    closeOnClick: true,
                    draggable: true,
                });
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Failed to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, []);

    const exportReport = useCallback(() => {
        if (!watchedValues.cutOffDate) {
            toast.error('Please select a cut off date before exporting');
            return;
        }

        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });
        reportService
            .exportNumberOfActiveProgramsReport(moment(watchedValues.cutOffDate).format(), watchedValues.concentrations)
            .then((report) => {
                if (report) {
                    const url = window.URL.createObjectURL(report);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `ActiveProgramsReport.xlsx`;
                    a.click();
                    toast.update(toastId, {
                        render: 'Report generated successfully.',
                        type: 'success',
                        autoClose: 2500,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Unable to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, [watchedValues.cutOffDate, watchedValues.concentrations]);

    return (
        <>
            <Card>
                <CardBody>
                    <Row>
                        <Col>
                            <div className="mb-3">
                                <h3>Active Program Report</h3>
                            </div>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Controller
                                        name="concentrations"
                                        control={control}
                                        render={({ field }) => (
                                            <div className={'d-flex justify-content-between p-2'}>
                                                <div className={'custom-control custom-radio pr-2'}>
                                                    <input
                                                        type={'radio'}
                                                        id={`radioAll`}
                                                        className={'custom-control-input'}
                                                        checked={field.value === true}
                                                        onChange={() => field.onChange(true)}
                                                        value="1"
                                                    />
                                                    <label
                                                        className={'custom-control-label'}
                                                        htmlFor={`radioAll`}
                                                    >{`With Concentrations & Levels`}</label>
                                                </div>
                                                <div className={'custom-control custom-radio pr-2'}>
                                                    <input
                                                        type={'radio'}
                                                        id={`radioUnmerged`}
                                                        className={'custom-control-input'}
                                                        checked={field.value === false}
                                                        onChange={() => field.onChange(false)}
                                                        value="0"
                                                    />
                                                    <label
                                                        className={'custom-control-label'}
                                                        htmlFor={`radioUnmerged`}
                                                    >{`Without Concentrations & Levels`}</label>
                                                </div>
                                            </div>
                                        )}
                                    />
                                    {errors.concentrations && (
                                        <div className="text-danger small mt-1">{errors.concentrations.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="cutOffDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Cut Off Date:
                                    </Label>
                                    <Controller
                                        name="cutOffDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                    {errors.cutOffDate && (
                                        <div className="text-danger small mt-1">{errors.cutOffDate.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Button type="submit">Search</Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-2">
                        <Button type="button" color="info" onClick={() => exportReport()}>
                            Export Report
                        </Button>
                    </div>
                    <div className="mt-3">
                        <h4>Number of Active Programs</h4>
                    </div>
                </CardBody>
                <Table>
                    {actionTotals?.numberOfActiveProgramsByProfessionDtos ? (
                        actionTotals?.numberOfActiveProgramsByProfessionDtos.map((profession) => (
                            <tbody key={profession.professionId}>
                                <tr>
                                    <td className={`font-weight-bold`}>
                                        {profession.professionTitle} - {profession.totalCount}
                                    </td>
                                </tr>
                                {profession.activeProgramsByStatusDtos ? (
                                    profession.activeProgramsByStatusDtos.map((status) => (
                                        <div key={status.statusId}>
                                            <tr>
                                                <td>
                                                    {status.statusName} - {status.programCount}
                                                </td>
                                            </tr>
                                        </div>
                                    ))
                                ) : (
                                    <tr>
                                        <td />
                                    </tr>
                                )}
                                {profession.activeConcentrationsDtos ? (
                                    profession.activeConcentrationsDtos.map((concentration) => (
                                        <div key={concentration.concentrationId}>
                                            <tr>
                                                <td>
                                                    {concentration.concentrationTitle} - {concentration.programCount}
                                                </td>
                                            </tr>
                                        </div>
                                    ))
                                ) : (
                                    <tr>
                                        <td />
                                    </tr>
                                )}
                                {profession.activeLevelsDtos ? (
                                    profession.activeLevelsDtos.map((level) => (
                                        <div key={level.levelId}>
                                            <tr>
                                                <td>
                                                    {level.levelTitle} - {level.programCount}
                                                </td>
                                            </tr>
                                        </div>
                                    ))
                                ) : (
                                    <tr>
                                        <td />
                                    </tr>
                                )}
                            </tbody>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={1}>No Results</td>
                        </tr>
                    )}
                    <tr>
                        <td className={`font-weight-bold`}>Total Records: {actionTotals?.totalCount ?? 0}</td>
                    </tr>
                </Table>
            </Card>
        </>
    );
};

export default NumberOfActiveProgramsReport;
